import React, { useState } from 'react';
import PropTypes from 'prop-types';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { IconButton } from '@mui/material';
import { useDispatch } from 'react-redux';
import Logo from '../common/logo';
import PingIndicator from '../common/ping_indicator';
import MealBasketWidget from '../../features/end_user_shopping/components/meal_basket_widget';
import { signOutAction } from '../../action_creators/user_action_creators';
import {
  END_USER_RECIPES_ROUTE,
  END_USER_SHARED_PROGRAMMES_ROUTE,
  LOGIN_ROUTE,
  SMORG_STUDIO_MEMBERSHIP_TIERS_ROUTE,
  SMORG_STUDIO_MEMBERSHIPS_ROUTE,
  SMORG_STUDIO_SPACE_EDIT_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE,
  SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARDS_ROUTE,
} from '../../services/routes';
import { linkWithQueryParams, openOutsideLink } from '../../services/urls';
import AccountActionsPopupMenuTopnav from './account_actions_popup_menu_topnav';
import { userIsCreator } from '../../services/users';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetSharedProgrammesForEndUserQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const TopNavBar = ({ mealBasketWidgetEnabled }) => {
  const location = useLocation();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const { data: currentSpace } = useGetEndUserSpaceQuery();

  const handleSignOut = () => {
    onClosePopover();
    console.log('Signing out');
    dispatch(signOutAction());
    navigate(
      linkWithQueryParams(LOGIN_ROUTE, {
        intoSpace: currentSpace?.id,
      }),
    );
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const isSpaceContentSection =
    matchPath(
      SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARDS_ROUTE,
      location.pathname,
    ) ||
    matchPath(
      SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE,
      location.pathname,
    ) ||
    matchPath(
      SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE,
      location.pathname,
    );

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const { data: sharedProgrammes } = useGetSharedProgrammesForEndUserQuery();

  const anyProgrammesAvailable =
    sharedProgrammes && sharedProgrammes.length > 0;

  const isProgramsAvailable = !isCommunityUser && anyProgrammesAvailable;

  return (
    <>
      <div className="topNavBar">
        <div className="sections">
          <Logo userIsCreator={isSmorgStudio} />
          <Link
            to={END_USER_RECIPES_ROUTE}
            className={location.pathname.startsWith('/recipes') && 'current'}>
            Recipes
          </Link>
          {!isSmorgStudio && (
            <Link
              to="/planner"
              className={location.pathname.startsWith('/planner') && 'current'}>
              Planner
            </Link>
          )}
          {!isSmorgStudio && isProgramsAvailable && (
            <Link
              to={END_USER_SHARED_PROGRAMMES_ROUTE}
              className={
                location.pathname.startsWith(
                  END_USER_SHARED_PROGRAMMES_ROUTE,
                ) && 'current'
              }>
              Programs
            </Link>
          )}
          {!isSmorgStudio && (
            <Link
              to="/products"
              className={
                location.pathname.startsWith('/products') && 'current'
              }>
              Shopping Lists
            </Link>
          )}
          {isSmorgStudio && (
            <Link
              to="/programmes"
              className={
                location.pathname.startsWith('/programmes') && 'current'
              }>
              Programs
            </Link>
          )}
          {isSmorgStudio && (
            <Link
              to={SMORG_STUDIO_MEMBERSHIP_TIERS_ROUTE}
              className={
                location.pathname.startsWith(
                  SMORG_STUDIO_MEMBERSHIP_TIERS_ROUTE,
                ) && 'current'
              }>
              Memberships
            </Link>
          )}
          {isSmorgStudio && (
            <Link
              to={SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARDS_ROUTE}
              className={isSpaceContentSection && 'current'}>
              Content
            </Link>
          )}
          {isSmorgStudio && (
            <Link
              to={SMORG_STUDIO_MEMBERSHIPS_ROUTE}
              className={
                location.pathname.startsWith(SMORG_STUDIO_MEMBERSHIPS_ROUTE) &&
                'current'
              }>
              Manage Members
            </Link>
          )}
          {isSmorgStudio && (
            <Link
              to={SMORG_STUDIO_SPACE_EDIT_ROUTE}
              className={
                location.pathname.startsWith(SMORG_STUDIO_SPACE_EDIT_ROUTE) &&
                !isSpaceContentSection &&
                'current'
              }>
              Your App
            </Link>
          )}
          <PingIndicator />
        </div>
        <div className="globalLinks">
          {mealBasketWidgetEnabled && <MealBasketWidget />}
          <div>
            <IconButton
              style={{ color: 'var(--app-primary-font-color)' }}
              onClick={() => {
                openOutsideLink('https://help.mealiq.io');
              }}
              title="Help">
              <HelpOutlineIcon />
            </IconButton>
          </div>
          <div>
            <IconButton
              style={{ color: 'var(--app-primary-font-color)' }}
              onClick={(ev) => {
                setAnchorEl(ev.target);
              }}
              title="Account">
              <AccountCircleIcon />
            </IconButton>
          </div>
        </div>
      </div>
      <AccountActionsPopupMenuTopnav
        anchorEl={anchorEl}
        handleClose={onClosePopover}
        handleSignOut={handleSignOut}
      />
    </>
  );
};

TopNavBar.propTypes = {
  mealBasketWidgetEnabled: PropTypes.bool,
};

TopNavBar.defaultProps = {
  mealBasketWidgetEnabled: false,
};

export default TopNavBar;
